import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import { motion } from 'motion/react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import {
  CONDITION_MAP, COND_TIERS, calcProfit, getLivePrice, getStickerValue,
  buildStickerIndex, withStickerImages,
} from './helpers';
import { PaidWithField } from './PaidWith';
import StickerSlots from './StickerSlots';
import './ZoneTheme.css';
import { findSkinImage } from './skinIndex';

// Three alternative layouts for the item edit modal, all driven by the same
// form state so they can be compared live:
//   twopane  — wide split, purple accent, form on the right rail
//   receipt  — itemised receipt with a perforated stub down the side
//   zone     — the Overview 7 / Armory board: candy paper, hard shadows
// The shared logic lives in ItemFormModalAlt; the Layout components below are
// purely presentational.

// Zone palette — kept local so this file has no import cycle with the tabs.
const ZN = {
  black: '#131313', white: '#fffef8', yellow: '#ffd43d', pink: '#ff9bd2',
  hot: '#ff5c8a', teal: '#6fe8cf', lime: '#c9f65a', purple: '#b79cff',
  orange: '#ffab5e', green: '#3ddc84', win: '#0d7a43', lose: '#c8134b',
  mute: 'rgba(19,19,19,0.55)',
};

function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}

// Category colours are user-chosen in Settings, so a solid pill can land on
// anything from pale yellow to near-black. Pick the ink that stays readable.
function readableInk(hex) {
  let h = String(hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return ZN.black;
  const n = parseInt(h, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 0.42 ? ZN.black : ZN.white;
}


// ── Skin search ──────────────────────────────────────────────
function SkinSearch({ value, onChange, searchableOptions, accent }) {
  const [q, setQ]         = useState(value?.value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen]   = useState(false);
  const ref = useRef(null);

  useEffect(() => { setQ(value?.value || ''); }, [value]);

  useEffect(() => {
    const away = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const search = (text) => {
    const words = text.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!words.length) { setResults([]); setOpen(false); return; }
    const out = [];
    for (const opt of (searchableOptions || [])) {
      if (out.length >= 8) break;
      if (words.every(w => opt.value.toLowerCase().includes(w))) out.push(opt);
    }
    setResults(out);
    setOpen(out.length > 0);
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <input
        className="ialt-input ialt-input-lg"
        type="text"
        value={q}
        onChange={e => { setQ(e.target.value); search(e.target.value); }}
        onFocus={() => q && search(q)}
        placeholder={searchableOptions?.length ? 'Search for a skin…' : 'Loading skins…'}
        style={{ borderColor: open ? accent : undefined }}
      />
      {open && (
        <div className="ialt-drop">
          {results.map((r, i) => (
            <button key={i} type="button" className="ialt-drop-item"
              onMouseDown={() => { onChange({ value: r.value, label: r.value }); setQ(r.value); setOpen(false); }}>
              {r.value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Float / wear bar ─────────────────────────────────────────
// The CS2 wear gradient with a marker at the item's float. When no float is
// recorded it falls back to the midpoint of the selected condition's band so
// the bar still communicates which tier the item sits in.
function WearBar({ float, condition, accent }) {
  const tierIdx = COND_TIERS.findIndex(t => t.value === condition);
  let pos = null, exact = float != null && float !== '';
  if (exact) pos = Math.max(0, Math.min(1, parseFloat(float)));
  else if (tierIdx !== -1) {
    const lo = tierIdx === 0 ? 0 : COND_TIERS[tierIdx - 1].max;
    pos = (lo + COND_TIERS[tierIdx].max) / 2;
  }
  return (
    <div>
      <div className="ialt-wear-head">
        <span className="ialt-label" style={{ margin: 0 }}>Wear</span>
        <span className="ialt-mono" style={{ color: '#efe9f7' }}>
          {exact ? parseFloat(float).toFixed(6) : '—'}
          <span style={{ color: '#8b849c', marginLeft: 7 }}>{condition || 'not set'}</span>
        </span>
      </div>
      <div className="ialt-wear-track">
        {pos != null && (
          <motion.span
            className="ialt-wear-pin"
            style={{ boxShadow: `0 0 8px ${accent}` }}
            initial={{ left: '50%', opacity: 0 }}
            animate={{ left: `${pos * 100}%`, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
      </div>
      <div className="ialt-wear-scale">
        {COND_TIERS.map(t => <span key={t.value}>{t.short}</span>)}
      </div>
    </div>
  );
}

// ── Price history chart ──────────────────────────────────────
function HistoryChart({ form, buy, chartSrc, setChartSrc, accent, height = 110, zone = false }) {
  const chartData = form.priceHistory.map(p => ({
    date: p.date,
    buffPrice: p.buffPrice ?? p.price ?? null,
    csfloatPrice: p.csfloatPrice ?? null,
  }));
  const hasCSF    = chartData.some(p => p.csfloatPrice != null);
  const dataKey   = chartSrc === 'buff' ? 'buffPrice' : 'csfloatPrice';
  const color     = zone ? ZN.black : chartSrc === 'buff' ? '#34d399' : accent;
  const fillTone  = zone ? (chartSrc === 'buff' ? ZN.green : ZN.purple) : color;
  const tick      = zone
    ? { fill: ZN.black, fontSize: 13.5, fontWeight: 800 }
    : { fill: '#5f5872', fontSize: 13.5 };
  const srcLabel  = chartSrc === 'buff' ? 'Buff163' : 'CSFloat';
  const valid     = chartData.filter(p => p[dataKey] != null);
  const prices    = valid.map(p => p[dataKey]);
  const low       = prices.length ? Math.min(...prices) : null;
  const high      = prices.length ? Math.max(...prices) : null;
  const trend     = prices.length >= 2 && prices[0] !== 0
    ? (prices[prices.length - 1] - prices[0]) / prices[0] * 100 : null;

  return (
    <div className="ialt-card">
      <div className="ialt-card-head">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span className="ialt-label" style={{ margin: 0 }}>Price history</span>
          <span className="ialt-mono" style={{ color: '#6f6880', fontSize: 14.5 }}>
            {form.priceHistory.length} days
          </span>
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          {[{ id: 'buff', label: 'Buff163', c: '#34d399' }, { id: 'csfloat', label: 'CSFloat', c: accent }].map(src => {
            const on = chartSrc === src.id;
            const disabled = src.id === 'csfloat' && !hasCSF;
            return (
              <button key={src.id} type="button" className="ialt-srcbtn"
                onClick={() => !disabled && setChartSrc(src.id)}
                disabled={disabled}
                style={on ? { borderColor: src.c + '59', background: src.c + '1a', color: src.c } : undefined}>
                {src.label}
              </button>
            );
          })}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={valid} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="ialtGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={fillTone} stopOpacity={zone ? 0.7 : 0.22} />
              <stop offset="95%" stopColor={fillTone} stopOpacity={zone ? 0.05 : 0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray={zone ? undefined : '3 3'}
            stroke={zone ? 'rgba(19,19,19,0.14)' : 'rgba(255,255,255,0.05)'} vertical={false} />
          <XAxis dataKey="date" tick={tick} axisLine={zone ? { stroke: ZN.black, strokeWidth: 3 } : false} tickLine={false}
            interval="preserveStartEnd" tickFormatter={d => d.slice(5)} />
          <YAxis tick={tick} axisLine={zone ? { stroke: ZN.black, strokeWidth: 3 } : false} tickLine={false}
            tickFormatter={v => `$${v}`} width={44} domain={['auto', 'auto']} />
          <Tooltip formatter={v => [`$${parseFloat(v).toFixed(2)}`, srcLabel]}
            contentStyle={zone
              ? { background: ZN.white, border: `3px solid ${ZN.black}`, borderRadius: 0,
                  boxShadow: `4px 4px 0 ${ZN.black}`, fontSize: 15, fontWeight: 800, fontFamily: 'Archivo, sans-serif' }
              : { background: '#100d18', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, fontSize: '0.94rem' }}
            labelStyle={{ color: zone ? ZN.mute : '#8b849c' }} />
          {buy > 0 && <ReferenceLine y={buy} stroke={zone ? ZN.black : 'rgba(245,158,11,0.6)'} strokeDasharray="4 3"
            label={{ value: `cost $${buy.toFixed(2)}`, fill: zone ? ZN.black : '#f59e0b', fontSize: 13.5, position: 'insideTopLeft' }} />}
          <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={zone ? 3 : 2}
            fill="url(#ialtGrad)" dot={false}
            activeDot={zone
              ? { fill: ZN.yellow, stroke: ZN.black, strokeWidth: 3, r: 5 }
              : { fill: color, r: 3, strokeWidth: 0 }} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="ialt-chart-foot">
        <span>low {low != null ? `$${low.toFixed(2)}` : 'n/a'}</span>
        <span style={{ color: trend == null ? '#5f5872' : trend >= 0 ? '#34d399' : '#f87171' }}>
          {trend == null ? 'no trend yet' : `trend ${trend >= 0 ? '+' : '−'}${Math.abs(trend).toFixed(1)}%`}
        </span>
        <span>high {high != null ? `$${high.toFixed(2)}` : 'n/a'}</span>
      </div>
    </div>
  );
}

// ── Shared bits ──────────────────────────────────────────────
const selectStyles = (accent) => ({
  menuPortal: base => ({ ...base, zIndex: 100000 }),
  control: base => ({
    ...base, background: '#100d18', borderColor: 'rgba(255,255,255,0.1)',
    borderRadius: 10, minHeight: 42, boxShadow: 'none',
    ':hover': { borderColor: accent + '66' },
  }),
  singleValue: base => ({ ...base, color: '#efe9f7', fontWeight: 600, fontSize: '0.98rem' }),
  placeholder:  base => ({ ...base, color: '#5f5872', fontSize: '0.98rem' }),
  input:        base => ({ ...base, color: '#efe9f7' }),
  menu:         base => ({ ...base, background: '#100d18', border: '1px solid rgba(255,255,255,0.12)' }),
  option: (base, s) => ({
    ...base, background: s.isFocused ? 'rgba(255,255,255,0.06)' : 'transparent',
    color: '#efe9f7', fontSize: '0.98rem',
  }),
});

function TypePills({ form, settings, set, accent, zone = false }) {
  const formTypes = getItemTypes(form);
  return (
    <div className="ialt-pills">
      {(settings?.itemTypes || []).map(type => {
        const on = formTypes.some(t => t.value === type.value);
        // On the cream board a 16%-alpha tint reads as "off"; a selected
        // category gets the colour solid, with ink text and a pressed shadow.
        const onStyle = zone
          ? { background: type.color, color: readableInk(type.color), borderColor: ZN.black, boxShadow: `2px 2px 0 ${ZN.black}` }
          : { borderColor: type.color, background: type.color + '29', color: type.color };
        return (
          <button key={type.value} type="button" className={`ialt-pill${on ? ' on' : ''}`}
            onClick={() => set('itemType', on
              ? formTypes.filter(t => t.value !== type.value)
              : [...formTypes, { value: type.value, label: type.label, color: type.color }])}
            style={on ? onStyle : undefined}>
            {type.label}
          </button>
        );
      })}
      {(settings?.itemTypes || []).length === 0 && (
        <span style={{ fontSize: 15, color: '#5f5872' }}>No categories configured</span>
      )}
    </div>
  );
}

function CondPills({ form, set, hasCondition, accent, zone = false }) {
  return (
    <div className="ialt-pills">
      {COND_TIERS.map(c => {
        const on = form.condition?.value === c.value;
        const onStyle = zone
          ? { background: ZN.yellow, color: ZN.black, borderColor: ZN.black, boxShadow: `2px 2px 0 ${ZN.black}` }
          : { borderColor: accent, background: accent + '29', color: '#fff' };
        return (
          <button key={c.value} type="button" className={`ialt-pill${on && hasCondition ? ' on' : ''}`} title={c.value}
            disabled={!hasCondition}
            onClick={() => hasCondition && set('condition', { value: c.value, label: c.value })}
            style={on && hasCondition ? onStyle : undefined}>
            {c.short}
          </button>
        );
      })}
      {!hasCondition && <span className="ialt-note">Not applicable</span>}
    </div>
  );
}

// What is applied to the item, with what the stickers are worth. Rendered by
// all three layouts, and by nothing at all when the item is bare — an empty row
// of slots on a knife would be five boxes of noise.
function StickerBlock({ form, livePrices, stickerIndex, zone = false }) {
  const applied = (form?.stickers || []).filter(s => s?.name);
  if (!applied.length) return null;
  const { total, complete } = getStickerValue(form, livePrices);
  return (
    <div>
      <div className="ialt-label" style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <span>Stickers · {applied.length}</span>
        {total > 0 && (
          <span style={{ color: zone ? ZN.win : '#f59e0b' }}>
            ${total.toFixed(2)}{complete ? '' : '+'}
          </span>
        )}
      </div>
      <StickerSlots stickers={withStickerImages(form.stickers, stickerIndex)} prices={livePrices}
        variant={zone ? 'zone' : 'classic'} maxWidth={zone ? 320 : 270}
        style={{ marginBottom: 0 }} />
    </div>
  );
}

// ── Layout: Two Pane ─────────────────────────────────────────
function TwoPaneLayout(p) {
  const A = '#8b5cf6';
  const { form, set, img, mktPrice, buy, headline, headlineRoi, isSold, hasCondition,
          settings, searchableOptions, isNew, onClose, handleSave, handleDelete,
          confirmDelete, setConfirmDelete, chartSrc, setChartSrc, switcher, livePrices, stickerIndex } = p;
  const good = headline == null ? null : headline >= 0;

  return (
    <motion.div className="ialt ialt-twopane" onClick={e => e.stopPropagation()}
      initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}>

      <div className="ialt-top">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div className="ialt-top-title">{isNew ? 'Add position' : 'Edit position'}</div>
          <div className="ialt-mono ialt-top-meta">
            added {new Date(form.timestamp || Date.now()).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        </div>
        {switcher}
        <button className="ialt-x" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="ialt-split">
        {/* Left: identity and history */}
        <div className="ialt-left">
          <div className="ialt-hero">
            <div className="ialt-render">
              {img ? <img src={img} alt="" /> : <span className="ialt-mono">skin render</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ialt-label">Skin</div>
              <SkinSearch value={form.skin} onChange={v => set('skin', v)}
                searchableOptions={searchableOptions} accent={A} />
              <div style={{ marginTop: 10 }}>
                <TypePills form={form} settings={settings} set={set} accent={A} />
              </div>
            </div>
          </div>

          <div className="ialt-row-2">
            <WearBar float={form.float} condition={form.condition?.value} accent={A} />
            <div>
              <div className="ialt-label">Pattern</div>
              <input className="ialt-input ialt-mono" type="number" min="0" max="1000" step="1"
                value={form.paintSeed ?? ''} placeholder="0–1000"
                onChange={e => set('paintSeed', e.target.value ? parseInt(e.target.value) : null)} />
            </div>
          </div>

          <div>
            <div className="ialt-label">Condition</div>
            <CondPills form={form} set={set} hasCondition={hasCondition} accent={A} />
          </div>

          <StickerBlock form={form} livePrices={livePrices} stickerIndex={stickerIndex} />

          <div>
            <div className="ialt-label">Description</div>
            <textarea className="ialt-input ialt-area" rows={2} value={form.description || ''}
              onChange={e => set('description', e.target.value)}
              placeholder="Notes, sticker combos…" />
          </div>

          {form.priceHistory?.length >= 2 && !isNew && (
            <HistoryChart form={form} buy={buy} chartSrc={chartSrc} setChartSrc={setChartSrc} accent={A} />
          )}
        </div>

        {/* Right: the numbers */}
        <div className="ialt-right">
          <div className="ialt-pl" style={{
            borderColor: good === null ? 'rgba(255,255,255,0.1)' : good ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.3)',
            background:  good === null ? 'rgba(255,255,255,0.02)' : good ? 'rgba(52,211,153,0.06)' : 'rgba(248,113,113,0.06)',
          }}>
            <div className="ialt-label" style={{ color: good === null ? '#8b849c' : good ? '#6ee7b7' : '#fca5a5' }}>
              {isSold ? 'Realized P&L' : 'Unrealized P&L'}
            </div>
            <div className="ialt-pl-num ialt-mono" style={{ color: good === null ? '#8b849c' : good ? '#34d399' : '#f87171' }}>
              {headline == null ? 'n/a' : `${headline < 0 ? '−' : '+'}$${Math.abs(headline).toFixed(2)}`}
            </div>
            <div className="ialt-mono ialt-pl-sub">
              {headlineRoi != null && `${headlineRoi >= 0 ? '+' : '−'}${Math.abs(headlineRoi).toFixed(1)}% · `}
              {isSold
                ? `sold $${(parseFloat(form.sellPrice) || 0).toFixed(2)} vs cost $${buy.toFixed(2)}`
                : `mark ${mktPrice != null ? `$${mktPrice.toFixed(2)}` : 'n/a'} vs cost $${buy.toFixed(2)}`}
            </div>
          </div>

          <div>
            <div className="ialt-label">Buy price</div>
            <div className="ialt-money" style={{ borderColor: A + '66' }}>
              <span className="ialt-mono ialt-money-sign">$</span>
              <input className="ialt-money-input ialt-mono" type="number" step="0.01" min="0"
                value={form.buyPrice} placeholder="0.00"
                onChange={e => set('buyPrice', e.target.value)} />
            </div>
            {settings?.trackWallets !== false && (
              <div style={{ marginTop: 8 }}>
                <PaidWithField value={form.paidWith} onChange={v => set('paidWith', v)} accent={A}
                  allowBank={settings?.useBankWallet !== false} />
              </div>
            )}
          </div>

          <div>
            <div className="ialt-label">Sell price</div>
            <div className={`ialt-money ${isSold ? '' : 'dashed'}`}>
              <span className="ialt-mono ialt-money-sign">$</span>
              <input className="ialt-money-input ialt-mono" type="number" step="0.01" min="0"
                value={form.sellPrice} placeholder="empty while holding"
                onChange={e => set('sellPrice', e.target.value)} />
            </div>
          </div>

          <div>
            <div className="ialt-label">Platform</div>
            <Select value={form.siteFee} onChange={v => set('siteFee', v)}
              options={settings?.sites} isClearable classNamePrefix="Select"
              placeholder="Select platform…" menuPlacement="auto"
              menuPortalTarget={document.body} menuPosition="fixed"
              styles={selectStyles(A)} />
          </div>

          <div>
            <div className="ialt-label" style={{ color: '#ffc425' }}>Target price alert</div>
            <div className="ialt-money" style={{ borderColor: 'rgba(255,196,37,0.24)', background: 'rgba(255,196,37,0.05)' }}>
              <span className="ialt-mono ialt-money-sign">$</span>
              <input className="ialt-money-input ialt-mono" type="number" step="0.01" min="0"
                value={form.targetPrice || ''} placeholder="alert me at…"
                onChange={e => set('targetPrice', e.target.value)} />
            </div>
          </div>

          <div className="ialt-actions">
            {form.inspectLink && (
              <div style={{ display: 'flex', gap: 7 }}>
                <a className="ialt-linkbtn" href={form.inspectLink} target="_blank" rel="noreferrer" style={{ color: '#c4b5fd' }}>Inspect ↗</a>
                <a className="ialt-linkbtn" style={{ color: '#c4b5fd' }} target="_blank" rel="noreferrer"
                  href={`https://steamcommunity.com/market/listings/730/${encodeURIComponent((form.skin?.value || '') + (form.condition?.value ? ` (${CONDITION_MAP[form.condition.value] || form.condition.value})` : ''))}`}>Steam ↗</a>
              </div>
            )}
            <button className="ialt-save" onClick={handleSave} disabled={!form.skin}
              style={{ background: `linear-gradient(180deg, ${A}, #7c3aed)`, boxShadow: `0 8px 22px ${A}59` }}>
              {isNew ? 'Add item' : 'Save changes'}
            </button>
            <div className="ialt-foot-row">
              {!isNew ? (
                confirmDelete
                  ? <span style={{ display: 'flex', gap: 10 }}>
                      <button className="ialt-textbtn danger" onClick={handleDelete}>Confirm delete</button>
                      <button className="ialt-textbtn" onClick={() => setConfirmDelete(false)}>Keep</button>
                    </span>
                  : <button className="ialt-textbtn danger" onClick={() => setConfirmDelete(true)}>Delete</button>
              ) : <span />}
              <button className="ialt-textbtn" onClick={onClose}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ── Layout: Receipt Stub ─────────────────────────────────────
function ReceiptLayout(p) {
  const A = '#ec4899';
  const { form, set, img, mktPrice, lp, buy, headline, headlineRoi, isSold, hasCondition,
          settings, searchableOptions, isNew, onClose, handleSave, handleDelete,
          confirmDelete, setConfirmDelete, chartSrc, setChartSrc, switcher, livePrices, stickerIndex } = p;
  const good = headline == null ? null : headline >= 0;
  const sell = parseFloat(form.sellPrice) || 0;
  const feeRate = (form.siteFee?.salesFee || 0) + (form.siteFee?.withdrawalFee || 0);
  const fees = sell * feeRate;

  const Line = ({ label, value, muted, accent: acc }) => (
    <div className="ialt-rc-line">
      <span className="ialt-rc-key" style={acc ? { color: acc } : undefined}>{label}</span>
      <span className="ialt-rc-dots" style={acc ? { borderColor: acc + '4d' } : undefined} />
      <span className={`ialt-rc-val ialt-mono ${muted ? 'muted' : ''}`}>{value}</span>
    </div>
  );

  return (
    <motion.div className="ialt ialt-receipt" onClick={e => e.stopPropagation()}
      initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}>

      {/* Receipt body */}
      <div className="ialt-rc-main">
        <div className="ialt-rc-head">
          <div style={{ minWidth: 0 }}>
            <div className="ialt-label" style={{ letterSpacing: '0.24em', margin: 0 }}>
              {isSold ? 'Sold' : 'Holding'} · {isNew ? 'new' : 'edit'}
            </div>
            <div className="ialt-rc-title">{form.skin?.value || 'Pick a skin'}</div>
          </div>
          {switcher}
          <button className="ialt-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="ialt-hero">
          <div className="ialt-render lg">
            {img ? <img src={img} alt="" /> : <span className="ialt-mono">skin render</span>}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <SkinSearch value={form.skin} onChange={v => set('skin', v)}
              searchableOptions={searchableOptions} accent={A} />
            <WearBar float={form.float} condition={form.condition?.value} accent={A} />
            <CondPills form={form} set={set} hasCondition={hasCondition} accent={A} />
            <div className="ialt-rc-facts ialt-mono">
              <span>pattern <b>{form.paintSeed ?? '—'}</b></span>
              <span>buff <b style={{ color: '#34d399' }}>{mktPrice != null ? `$${mktPrice.toFixed(2)}` : 'n/a'}</b></span>
              <span>order <b>{lp?.highest_order?.price != null ? `$${lp.highest_order.price.toFixed(2)}` : 'n/a'}</b></span>
            </div>
            <StickerBlock form={form} livePrices={livePrices} stickerIndex={stickerIndex} />
          </div>
        </div>

        {/* Itemised money lines, each editable inline */}
        <div className="ialt-rc-ledger">
          <div className="ialt-rc-line">
            <span className="ialt-rc-key">Bought for</span>
            <span className="ialt-rc-dots" />
            <input className="ialt-rc-input ialt-mono" type="number" step="0.01" min="0"
              value={form.buyPrice} placeholder="0.00"
              onChange={e => set('buyPrice', e.target.value)} />
          </div>
          {settings?.trackWallets !== false && (
            <div style={{ margin: '2px 0 6px' }}>
              <PaidWithField value={form.paidWith} onChange={v => set('paidWith', v)} accent="#b45309"
                allowBank={settings?.useBankWallet !== false} />
            </div>
          )}
          <div className="ialt-rc-line">
            <span className="ialt-rc-key">Sold for</span>
            <span className="ialt-rc-dots" />
            <input className="ialt-rc-input ialt-mono" type="number" step="0.01" min="0"
              value={form.sellPrice} placeholder="holding"
              onChange={e => set('sellPrice', e.target.value)} />
          </div>
          <Line
            label={`Fees${feeRate ? ` (${(feeRate * 100).toFixed(2)}%)` : ''}`}
            value={sell > 0 ? `−$${fees.toFixed(2)}` : 'n/a'}
            muted={!(sell > 0)}
          />
          <div className="ialt-rc-line total">
            <span className="ialt-rc-key" style={{ color: '#efe9f7' }}>{isSold ? 'Profit' : 'If sold at mark'}</span>
            <span className="ialt-rc-dots" />
            <span className="ialt-rc-val ialt-mono" style={{ color: good === null ? '#8b849c' : good ? '#34d399' : '#f87171', fontSize: 17 }}>
              {headline == null ? 'n/a' : `${headline < 0 ? '−' : '+'}$${Math.abs(headline).toFixed(2)}`}
            </span>
          </div>
        </div>

        <div className="ialt-row-2">
          <div>
            <div className="ialt-label">Marketplace</div>
            <Select value={form.siteFee} onChange={v => set('siteFee', v)}
              options={settings?.sites} isClearable classNamePrefix="Select"
              placeholder="Select platform…" menuPlacement="auto"
              menuPortalTarget={document.body} menuPosition="fixed"
              styles={selectStyles(A)} />
          </div>
          <div>
            <div className="ialt-label" style={{ color: '#ffc425' }}>Alert me at</div>
            <div className="ialt-money" style={{ borderColor: 'rgba(255,196,37,0.24)', background: 'rgba(255,196,37,0.05)' }}>
              <span className="ialt-mono ialt-money-sign">$</span>
              <input className="ialt-money-input ialt-mono" type="number" step="0.01" min="0"
                value={form.targetPrice || ''} placeholder="not set"
                onChange={e => set('targetPrice', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="ialt-row-2">
          <div>
            <div className="ialt-label">Category</div>
            <TypePills form={form} settings={settings} set={set} accent={A} />
          </div>
          <div>
            <div className="ialt-label">Pattern</div>
            <input className="ialt-input ialt-mono" type="number" min="0" max="1000" step="1"
              value={form.paintSeed ?? ''} placeholder="0–1000"
              onChange={e => set('paintSeed', e.target.value ? parseInt(e.target.value) : null)} />
          </div>
        </div>

        <div>
          <div className="ialt-label">Notes</div>
          <textarea className="ialt-input ialt-area" rows={2} value={form.description || ''}
            onChange={e => set('description', e.target.value)}
            placeholder="Sticker combos, trade context…" />
        </div>

        {form.priceHistory?.length >= 2 && !isNew && (
          <HistoryChart form={form} buy={buy} chartSrc={chartSrc} setChartSrc={setChartSrc} accent={A} height={80} />
        )}
      </div>

      {/* Perforation */}
      <div className="ialt-perf" aria-hidden="true" />

      {/* Stub */}
      <div className="ialt-stub">
        <div>
          <div className="ialt-label" style={{ letterSpacing: '0.24em', margin: 0 }}>Stub</div>
          <div className="ialt-mono ialt-stub-no">
            {(form.assetId || '').toString().slice(-6) || 'NEW'} · {new Date(form.timestamp || Date.now()).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}
          </div>
        </div>

        <div>
          <div className="ialt-label" style={{ letterSpacing: '0.2em' }}>Cost basis</div>
          <div className="ialt-mono ialt-stub-big">${buy.toFixed(2)}</div>
        </div>

        <div className="ialt-stub-pl">
          <div className="ialt-label" style={{ letterSpacing: '0.2em', color: good === null ? '#8b849c' : good ? '#6ee7b7' : '#fca5a5' }}>
            {isSold ? 'Realized P&L' : 'Unrealized P&L'}
          </div>
          <div className="ialt-mono ialt-stub-mid" style={{ color: good === null ? '#8b849c' : good ? '#34d399' : '#f87171' }}>
            {headline == null ? 'n/a' : `${headline < 0 ? '−' : '+'}$${Math.abs(headline).toFixed(2)}`}
          </div>
          <div className="ialt-mono ialt-stub-sub">
            {isSold ? `sold $${sell.toFixed(2)}` : `mark ${mktPrice != null ? `$${mktPrice.toFixed(2)}` : 'n/a'}`}
            {headlineRoi != null && ` · ${headlineRoi >= 0 ? '+' : '−'}${Math.abs(headlineRoi).toFixed(1)}%`}
          </div>
        </div>

        {form.inspectLink && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <a className="ialt-linkbtn" href={form.inspectLink} target="_blank" rel="noreferrer" style={{ color: '#f9a8d4' }}>Inspect in game ↗</a>
            <a className="ialt-linkbtn" style={{ color: '#f9a8d4' }} target="_blank" rel="noreferrer"
              href={`https://steamcommunity.com/market/listings/730/${encodeURIComponent((form.skin?.value || '') + (form.condition?.value ? ` (${CONDITION_MAP[form.condition.value] || form.condition.value})` : ''))}`}>Steam Market ↗</a>
          </div>
        )}

        <div className="ialt-stub-actions">
          <button className="ialt-save" onClick={handleSave} disabled={!form.skin}
            style={{ background: `linear-gradient(180deg, ${A}, #db2777)`, boxShadow: `0 8px 22px ${A}4d` }}>
            {isNew ? 'Add item' : 'Save changes'}
          </button>
          <div className="ialt-foot-row">
            {!isNew ? (
              confirmDelete
                ? <span style={{ display: 'flex', gap: 10 }}>
                    <button className="ialt-textbtn danger" onClick={handleDelete}>Confirm</button>
                    <button className="ialt-textbtn" onClick={() => setConfirmDelete(false)}>Keep</button>
                  </span>
                : <button className="ialt-textbtn danger" onClick={() => setConfirmDelete(true)}>Delete</button>
            ) : <span />}
            <button className="ialt-textbtn" onClick={onClose}>Cancel</button>
          </div>
          <div className="ialt-barcode" aria-hidden="true" />
        </div>
      </div>
    </motion.div>
  );
}

// ── Layout: Zone board ───────────────────────────────────────
// The Overview 7 / Armory look: cream board, 3px ink borders, hard offset
// shadows and candy panels. Uses the same shared atoms as the other two
// layouts — the .ialt-zone scope in ZoneTheme.css re-skins them.
// NOTE: this select uses classNamePrefix="znSelect", not "Select". The global
// .Select__* rules in index.css are all !important — they would override these
// inline styles and repaint the control in dark-theme colours, and because the
// menu is portalled to <body> it can't be out-scoped by an ancestor selector.
// A different prefix simply keeps those rules from matching.
const znSelectStyles = {
  menuPortal: base => ({ ...base, zIndex: 100000 }),
  control: (base, s) => ({
    ...base,
    background: ZN.white,
    border: `3px solid ${ZN.black}`,
    borderRadius: 0,
    minHeight: 44,
    boxShadow: s.isFocused ? `3px 3px 0 ${ZN.black}` : 'none',
    ':hover': { border: `3px solid ${ZN.black}` },
  }),
  valueContainer: base => ({ ...base, padding: '2px 10px' }),
  singleValue: base => ({
    ...base, color: ZN.black, fontFamily: "'Archivo', sans-serif",
    fontWeight: 800, fontSize: '0.98rem',
  }),
  placeholder: base => ({
    ...base, color: 'rgba(19,19,19,0.38)', fontSize: '0.97rem', fontWeight: 700,
  }),
  input: base => ({ ...base, color: ZN.black, fontWeight: 700 }),
  menu: base => ({
    ...base, background: ZN.white, border: `3px solid ${ZN.black}`,
    borderRadius: 0, boxShadow: `5px 5px 0 ${ZN.black}`, marginTop: 5, overflow: 'hidden',
  }),
  menuList: base => ({ ...base, padding: 0, maxHeight: 220 }),
  option: (base, s) => ({
    ...base,
    background: s.isSelected ? ZN.yellow : s.isFocused ? ZN.teal : ZN.white,
    color: ZN.black,
    fontFamily: "'Archivo', sans-serif",
    fontSize: '0.97rem',
    fontWeight: s.isSelected ? 800 : 700,
    borderBottom: '2px dashed rgba(19,19,19,0.18)',
    cursor: 'pointer',
    ':active': { background: ZN.lime },
  }),
  noOptionsMessage: base => ({ ...base, color: ZN.mute, fontWeight: 700, fontSize: '0.96rem' }),
  indicatorSeparator: base => ({ ...base, backgroundColor: ZN.black, width: 3, marginTop: 0, marginBottom: 0 }),
  dropdownIndicator: base => ({ ...base, color: ZN.black, ':hover': { color: ZN.black } }),
  clearIndicator:    base => ({ ...base, color: ZN.black, ':hover': { color: ZN.lose } }),
};

function ZoneLayout(p) {
  const { form, set, img, mktPrice, lp, buy, headline, headlineRoi, isSold, hasCondition,
          settings, searchableOptions, isNew, onClose, handleSave, handleDelete,
          confirmDelete, setConfirmDelete, chartSrc, setChartSrc, switcher, livePrices, stickerIndex } = p;

  const good = headline == null ? null : headline >= 0;
  const sell = parseFloat(form.sellPrice) || 0;
  const feeRate = (form.siteFee?.salesFee || 0) + (form.siteFee?.withdrawalFee || 0);
  const fees = sell * feeRate;
  const money = v => `$${Math.abs(v).toFixed(2)}`;
  const plColor = good === null ? ZN.mute : good ? ZN.win : ZN.lose;

  return (
    <motion.div className="ialt ialt-zone" onClick={e => e.stopPropagation()}
      initial={{ opacity: 0, y: -22, rotate: -1.2, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}>

      {/* ── Header ─────────────────────────────────── */}
      <div className="iz-top">
        <div className="iz-title">
          {isNew ? <>ADD <span className="hl">ITEM</span></> : <>EDIT <span className="hl">ITEM</span></>}
        </div>
        <span className="zn-sticker" style={{ background: isSold ? ZN.lime : ZN.teal, transform: 'rotate(-1.5deg)' }}>
          {isSold ? 'sold' : 'holding'}
        </span>
        <span className="zn-sticker" style={{ background: ZN.white, transform: 'rotate(1.5deg)' }}>
          {new Date(form.timestamp || Date.now()).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
        <div className="iz-top-right">
          {switcher}
          <button className="iz-x" onClick={onClose} aria-label="Close">×</button>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────── */}
      <div className="iz-split">

        {/* Left: what the item is */}
        <div className="iz-left">
          <div className="iz-hero">
            <div className="iz-render">
              {img ? <img src={img} alt="" /> : <span className="iz-render-empty">no render</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ialt-label">Skin</div>
              <SkinSearch value={form.skin} onChange={v => set('skin', v)}
                searchableOptions={searchableOptions} accent={ZN.black} />
              <div style={{ marginTop: 12 }}>
                <div className="ialt-label">Category</div>
                <TypePills form={form} settings={settings} set={set} accent={ZN.black} zone />
              </div>
            </div>
          </div>

          <div className="iz-panel">
            <WearBar float={form.float} condition={form.condition?.value} accent={ZN.black} />
          </div>

          <StickerBlock form={form} livePrices={livePrices} stickerIndex={stickerIndex} zone />


          <div className="ialt-row-2">
            <div>
              <div className="ialt-label">Condition</div>
              <CondPills form={form} set={set} hasCondition={hasCondition} accent={ZN.black} zone />
            </div>
            <div>
              <div className="ialt-label">Pattern</div>
              <input className="ialt-input ialt-mono" type="number" min="0" max="1000" step="1"
                value={form.paintSeed ?? ''} placeholder="0–1000"
                onChange={e => set('paintSeed', e.target.value ? parseInt(e.target.value) : null)} />
            </div>
          </div>

          <div>
            <div className="ialt-label">Notes</div>
            <textarea className="ialt-input ialt-area" rows={2} value={form.description || ''}
              onChange={e => set('description', e.target.value)}
              placeholder="Sticker combos, trade context…" />
          </div>

          {form.priceHistory?.length >= 2 && !isNew && (
            <div className="iz-chart">
              <HistoryChart form={form} buy={buy} chartSrc={chartSrc} setChartSrc={setChartSrc}
                accent={ZN.black} height={104} zone />
            </div>
          )}
        </div>

        {/* Right: what it's worth */}
        <div className="iz-right">
          <div className="iz-pl" style={{ background: good === null ? ZN.white : good ? ZN.lime : ZN.pink }}>
            <span className="iz-pl-tag">{isSold ? 'Realized P&L' : 'Unrealized P&L'}</span>
            <div className="iz-pl-num" style={{ color: plColor }}>
              {headline == null ? 'n/a' : `${headline < 0 ? '-' : '+'}${money(headline)}`}
            </div>
            <div className="iz-pl-sub">
              {headlineRoi != null && `${headlineRoi >= 0 ? '+' : '-'}${Math.abs(headlineRoi).toFixed(1)}% · `}
              {isSold
                ? `sold ${money(sell)} vs cost ${money(buy)}`
                : `mark ${mktPrice != null ? money(mktPrice) : 'n/a'} vs cost ${money(buy)}`}
            </div>
          </div>

          <div className="ialt-row-2">
            <div>
              <div className="ialt-label">Buy price</div>
              <div className="ialt-money">
                <span className="ialt-money-sign">$</span>
                <input className="ialt-money-input ialt-mono" type="number" step="0.01" min="0"
                  value={form.buyPrice} placeholder="0.00"
                  onChange={e => set('buyPrice', e.target.value)} />
              </div>
              {settings?.trackWallets !== false && (
                <div style={{ marginTop: 8 }}>
                  <PaidWithField value={form.paidWith} onChange={v => set('paidWith', v)} accent="#131313" zone
                    allowBank={settings?.useBankWallet !== false} />
                </div>
              )}
            </div>
            <div>
              <div className="ialt-label">Sell price</div>
              <div className={`ialt-money ${isSold ? '' : 'dashed'}`}>
                <span className="ialt-money-sign">$</span>
                <input className="ialt-money-input ialt-mono" type="number" step="0.01" min="0"
                  value={form.sellPrice} placeholder="holding"
                  onChange={e => set('sellPrice', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Live market facts */}
          <div className="iz-facts">
            <div><span className="k">Buff ask</span><span className="v">{mktPrice != null ? money(mktPrice) : 'n/a'}</span></div>
            <div><span className="k">Top order</span><span className="v">{lp?.highest_order?.price != null ? money(lp.highest_order.price) : 'n/a'}</span></div>
            <div>
              <span className="k">Fees{feeRate ? ` (${(feeRate * 100).toFixed(1)}%)` : ''}</span>
              <span className="v">{sell > 0 ? `-${money(fees)}` : 'n/a'}</span>
            </div>
          </div>

          <div>
            <div className="ialt-label">Platform</div>
            <Select value={form.siteFee} onChange={v => set('siteFee', v)}
              options={settings?.sites} isClearable classNamePrefix="znSelect"
              placeholder="Select platform…" menuPlacement="auto"
              menuPortalTarget={document.body} menuPosition="fixed"
              styles={znSelectStyles} />
          </div>

          <div>
            <div className="ialt-label">Target price alert</div>
            <div className="ialt-money" style={{ background: ZN.yellow }}>
              <span className="ialt-money-sign">$</span>
              <input className="ialt-money-input ialt-mono" type="number" step="0.01" min="0"
                value={form.targetPrice || ''} placeholder="alert me at…"
                onChange={e => set('targetPrice', e.target.value)} />
            </div>
          </div>

          {form.inspectLink && (
            <div className="iz-links">
              <a className="zn-btn" href={form.inspectLink} target="_blank" rel="noreferrer">Inspect ↗</a>
              <a className="zn-btn" target="_blank" rel="noreferrer"
                href={`https://steamcommunity.com/market/listings/730/${encodeURIComponent((form.skin?.value || '') + (form.condition?.value ? ` (${CONDITION_MAP[form.condition.value] || form.condition.value})` : ''))}`}>
                Steam ↗
              </a>
            </div>
          )}

          <div className="iz-actions">
            <button className="iz-save" onClick={handleSave} disabled={!form.skin}>
              {isNew ? 'Add item' : 'Save changes'}
            </button>
            <div className="iz-foot">
              {!isNew ? (
                confirmDelete
                  ? <span style={{ display: 'flex', gap: 8 }}>
                      <button className="zn-btn danger" onClick={handleDelete}>Confirm delete</button>
                      <button className="zn-btn" onClick={() => setConfirmDelete(false)}>Keep</button>
                    </span>
                  : <button className="zn-btn danger" onClick={() => setConfirmDelete(true)}>Delete</button>
              ) : <span />}
              <button className="zn-btn" onClick={onClose}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ── Shell: shared state for all layouts ──────────────────────
export default function ItemFormModalAlt({
  design, isNew, initialRow, realIndex, onSave, onClose, onDelete,
  settings, searchableOptions, skinsData, livePrices, itemsWithConditions, switcher,
}) {
  const [form, setForm] = useState({ ...initialRow });
  const [chartSrc, setChartSrc] = useState('buff');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const hasCondition = !form.skin?.value || itemsWithConditions?.has(form.skin.value);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', esc); };
  }, [onClose]);

  const profit = useMemo(
    () => calcProfit(form.buyPrice, form.sellPrice, form.siteFee),
    [form.buyPrice, form.sellPrice, form.siteFee],
  );

  // Stickers on a row rebuilt from trade history carry names but no pictures.
  const stickerIndex = useMemo(() => buildStickerIndex(skinsData), [skinsData]);

  const img      = findSkinImage(skinsData, form.skin?.value, form.condition?.value);
  const lp       = getLivePrice(livePrices, form);
  const mktPrice = lp?.starting_at?.price;
  const buy      = parseFloat(form.buyPrice) || 0;

  const set = (field, val) => setForm(f => {
    const next = { ...f, [field]: val };
    if (field === 'skin' && val && !itemsWithConditions?.has(val.value)) next.condition = null;
    return next;
  });

  const handleSave = () => {
    if (!form.skin) return;
    onSave({ ...form, profit: profit || '0' }, realIndex);
    onClose();
  };
  const handleDelete = () => { if (realIndex !== -1) onDelete(realIndex); onClose(); };

  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); handleSave(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const profitVal  = profit !== '' ? parseFloat(profit) : null;
  const isSold     = profitVal !== null;
  const unrealized = mktPrice != null && buy > 0 ? mktPrice - buy : null;
  const headline   = isSold ? profitVal : unrealized;
  const headlineRoi = headline != null && buy > 0 ? (headline / buy * 100) : null;

  const ctx = {
    form, set, img, lp, mktPrice, buy, profit, isSold, headline, headlineRoi, hasCondition,
    settings, searchableOptions, isNew, onClose, handleSave, handleDelete,
    confirmDelete, setConfirmDelete, chartSrc, setChartSrc, switcher, livePrices, stickerIndex,
  };

  const Layout = design === 'zone' ? ZoneLayout
    : design === 'receipt' ? ReceiptLayout
    : TwoPaneLayout;

  return (
    <div className={`modal-backdrop${design === 'zone' ? ' iz-backdrop' : ''}`} onClick={onClose}>
      <Layout {...ctx} />
    </div>
  );
}
